import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NetstaffLogo } from "@/components/NetstaffLogo";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatApiErrorDetail, api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { POSITION_NAMES } from "@/lib/constants";

export default function Register() {
  const { register, login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "", role: "professional",
    facility_name: "", facility_type: "Hospital", facility_address: "",
    facility_city: "Toronto", facility_postal: "",
    primary_position: "Personal Support Worker", city: "Toronto", hourly_rate: 28,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      // For facility / professional, also create the entity first
      let facility_id, professional_id;
      if (form.role === "facility") {
        const { data: f } = await api.post("/facilities", {
          name: form.facility_name,
          facility_type: form.facility_type,
          address: form.facility_address,
          city: form.facility_city,
          province: "Ontario",
          postal_code: form.facility_postal,
          contact_person: form.name,
          contact_number: form.phone || "+1-416-000-0000",
        });
        facility_id = f.id;
      } else if (form.role === "professional") {
        const [fn, ...rest] = form.name.split(" ");
        const ln = rest.join(" ") || "—";
        const { data: p } = await api.post("/professionals", {
          first_name: fn, last_name: ln,
          email: form.email, phone: form.phone || "+1-416-000-0000",
          city: form.city, postal_code: "M5V 0A1",
          primary_position: form.primary_position,
          hourly_rate: Number(form.hourly_rate),
        }).catch(() => ({ data: null }));
        professional_id = p?.id;
      }

      const user = await register({
        email: form.email, password: form.password, name: form.name,
        role: form.role, phone: form.phone, facility_id, professional_id,
      });
      toast.success(`Welcome, ${user.name}!`);
      if (user.role === "facility") navigate("/facility");
      else if (user.role === "professional") navigate("/pro");
      else navigate("/dispatch");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen hero-gradient flex items-center justify-center p-6 py-12">
      <div className="w-full max-w-xl">
        <Link to="/" className="block mb-6"><NetstaffLogo /></Link>
        <Card className="border-slate-200 shadow-xl shadow-brand-navy/5">
          <CardHeader>
            <CardTitle className="text-2xl text-brand-navy" style={{ fontFamily: "Outfit, sans-serif" }}>Create account</CardTitle>
            <p className="text-sm text-slate-500">Join the NetStaff network</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <Label>I am a…</Label>
                <Select value={form.role} onValueChange={(v) => upd("role", v)}>
                  <SelectTrigger data-testid="register-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="professional">Healthcare Professional</SelectItem>
                    <SelectItem value="facility">Healthcare Facility</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Full name</Label>
                  <Input value={form.name} onChange={(e) => upd("name", e.target.value)} required data-testid="register-name-input" />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={form.phone} onChange={(e) => upd("phone", e.target.value)} placeholder="+1-416-..." data-testid="register-phone-input" />
                </div>
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} required data-testid="register-email-input" />
              </div>
              <div>
                <Label>Password (min 6 chars)</Label>
                <Input type="password" value={form.password} onChange={(e) => upd("password", e.target.value)} required minLength={6} data-testid="register-password-input" />
              </div>

              {form.role === "facility" && (
                <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                  <div className="text-xs uppercase tracking-wider text-brand-teal font-semibold">Facility details</div>
                  <Input placeholder="Facility name" value={form.facility_name} onChange={(e) => upd("facility_name", e.target.value)} required data-testid="register-facility-name" />
                  <Select value={form.facility_type} onValueChange={(v) => upd("facility_type", v)}>
                    <SelectTrigger data-testid="register-facility-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["Hospital", "Long-Term Care Home", "Retirement Home", "Clinic", "Community Health Centre", "Rehabilitation Facility", "Home Care Agency", "Dental Clinic", "Medical Office"].map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input placeholder="Street address" value={form.facility_address} onChange={(e) => upd("facility_address", e.target.value)} required data-testid="register-facility-address" />
                  <div className="grid grid-cols-2 gap-3">
                    <Input placeholder="City" value={form.facility_city} onChange={(e) => upd("facility_city", e.target.value)} required />
                    <Input placeholder="Postal code" value={form.facility_postal} onChange={(e) => upd("facility_postal", e.target.value)} required />
                  </div>
                </div>
              )}

              {form.role === "professional" && (
                <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                  <div className="text-xs uppercase tracking-wider text-brand-teal font-semibold">Your profile</div>
                  <Select value={form.primary_position} onValueChange={(v) => upd("primary_position", v)}>
                    <SelectTrigger data-testid="register-position-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {POSITION_NAMES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="grid grid-cols-2 gap-3">
                    <Input placeholder="City" value={form.city} onChange={(e) => upd("city", e.target.value)} />
                    <Input placeholder="Hourly rate (CAD)" type="number" value={form.hourly_rate} onChange={(e) => upd("hourly_rate", e.target.value)} data-testid="register-rate-input" />
                  </div>
                </div>
              )}

              {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="register-error">{error}</div>}
              <Button type="submit" className="w-full bg-brand-navy hover:bg-brand-blue h-11" disabled={loading} data-testid="register-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
              </Button>
            </form>
            <div className="mt-6 text-sm text-center text-slate-500">
              Already have an account? <Link to="/login" className="text-brand-teal font-semibold hover:underline" data-testid="register-to-login-link">Sign in</Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
